import { Dialog } from "primereact/dialog";
import { useDispatch, useSelector } from "react-redux";
import {
  clearMaintenanceRequestPopupData,
  setIsMaintenanceRequestsDetailsPopupOpen,
} from "src/redux/slices/maintenanceRequestsSlice";
import { useTranslation } from "react-i18next";
import {
  formatDateV2,
  getClassesBasedOnStatus,
  getFieldFieldValue,
  getStatusByStatusId,
  getTextValueById,
} from "src/utils/helpers";

import SLABreached from "src/components/SLABreached/SLABreached";
import VerticalDivider from "src/components/VerticalDivider/VerticalDivider";
import { StatusesWithHoldReason } from "src/resources/enum";

const MaintenanceRequestsDetailsPopup = () => {
  const dispatch = useDispatch();

  const selectedMaintenanceRequest = useSelector(
    (state: any) => state.maintenanceRequests.selectedMaintenanceRequest
  );
  const isMaintenanceRequestsDetailsPopupOpen = useSelector(
    (state: any) =>
      state.maintenanceRequests.isMaintenanceRequestsDetailsPopupOpen
  );
  const {
    allServiceTerritories,
    systems,
    statusOptions,
    emiratesOptions,
    citiesOptions,
  } = useSelector((state: any) => state.lookup);

  const { t } = useTranslation(["common", "registration"]);
  const closePopup = (): void => {
    dispatch(setIsMaintenanceRequestsDetailsPopupOpen(false));
    dispatch(clearMaintenanceRequestPopupData());
  };

  const serviceTerritory = getTextValueById(
    selectedMaintenanceRequest?.serviceTerritoryId,
    allServiceTerritories as unknown as [],
    "territoryId"
  );

  const systemType = getTextValueById(
    selectedMaintenanceRequest?.systemServiceId,
    systems as unknown as [],
    "systemServiceId"
  );
  const StatusTemplate = () => {
    const status = getStatusByStatusId(
      statusOptions,
      selectedMaintenanceRequest?.statusCodeId
    );
    return (
      <span className={`oval ${getClassesBasedOnStatus(status)}`}>
        {getFieldFieldValue(status)}
      </span>
    );
  };

  function getEmirate() {
    return getTextValueById(
      selectedMaintenanceRequest.emirate,
      emiratesOptions,
      "emirateId"
    );
  }

  function getCity() {
    return getTextValueById(
      selectedMaintenanceRequest?.city,
      citiesOptions,
      "cityId"
    );
  }
  const status = getStatusByStatusId(
    statusOptions,
    selectedMaintenanceRequest?.statusCodeId
  );
  function checkIfPendingOwnerApproval() {
    return (
      status?.englishName?.toLowerCase() ===
      StatusesWithHoldReason.PendingOwnerApproval
    );
  }
  const getOnHoldReason = () => {
    return getFieldFieldValue(status);
  };

  return (
    <Dialog
      visible={isMaintenanceRequestsDetailsPopupOpen}
      onHide={closePopup}
      header={t("maintenanceRequestDetails")}
      headerClassName="popup-header"
      className="maintenance-requests-details-popup"
    >
      <div className="pt-3">
        <div className="d-flex justify-content-between flex-wrap gap-2 align-items-center mb-4 popup-top-section">
          <SLABreached sLABreached={selectedMaintenanceRequest?.sLABreached} />
        </div>
        <section>
          <h2 className="font-weight-700">{t("status")}</h2>
          <div className="section-body">
            <div className={`section-body__item  w-32 w-md-100 ${checkIfPendingOwnerApproval()? "py-0 my-auto" : ""}`}>
              <StatusTemplate />
            </div>
            {checkIfPendingOwnerApproval() && (
              <>
                <VerticalDivider />
                <div className="section-body__item">
                  <h4 className="font-weight-700">{t("typeOfApproval")}</h4>
                  <span>{getOnHoldReason()}</span>
                </div>{" "}
              </>
            )}
          </div>
        </section>
        <section>
          <h2 className="font-weight-700">{t("maintenanceRequestDetails")}</h2>
          <div className="section-body">
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("caseId")}</h4>
              <span>{selectedMaintenanceRequest?.ticketNumber}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("creationDate")}</h4>
              <span className="plain-text-date">
                {formatDateV2(selectedMaintenanceRequest?.createdOn)}
              </span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("systemName")}</h4>
              <span>{systemType}</span>
            </div>
          </div>

          <div className="section-body">
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("systemSubtype")}</h4>
              <span>{selectedMaintenanceRequest?.systemSubtype}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("faultLocation")}</h4>
              <span>{selectedMaintenanceRequest?.faultLocation}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item  w-32 w-md-100">
              <h4 className="font-weight-700">{t("faultDescription")}</h4>
              <span>{selectedMaintenanceRequest?.faultDescription}</span>
            </div>
          </div>
          <div className="section-body">
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("managerSupervisorName")}</h4>
              <span>{selectedMaintenanceRequest?.managerSupervisorName}</span>
            </div>
          </div>
        </section>
        <section>
          <h2 className="font-weight-700">{t("propertyDetails")}</h2>
          <div className="section-body">
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("propertyID")}</h4>
              <span>{selectedMaintenanceRequest?.propertyNumber}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("buildingNameHeader")}</h4>
              <span>{selectedMaintenanceRequest?.buildingName}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item">
              <h4 className="font-weight-700">{t("emirate")}</h4>
              <span>{getEmirate()}</span>
            </div>
          </div>
          <div className="section-body">
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("city")}</h4>
              <span>{getCity()}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("territory")}</h4>
              <span>{serviceTerritory}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-15 w-md-100">
              <h4 className="font-weight-700">{t("latitude")}</h4>
              <span>{selectedMaintenanceRequest?.latitude}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item w-15 w-md-100">
              <h4 className="font-weight-700">{t("longitude")}</h4>
              <span>{selectedMaintenanceRequest?.longitude}</span>
            </div>
          </div>
        </section>
        <section>
          <h2 className="font-weight-700">{t("contactInformation")}</h2>
          <div className="section-body">
            <div className="section-body__item w-32 w-md-100">
              <h4 className="font-weight-700">{t("bldgContactPerson")}</h4>
              <span>{selectedMaintenanceRequest?.buildingContactPerson}</span>
            </div>
            <VerticalDivider />
            <div className="section-body__item">
              <h4 className="font-weight-700">{t("phone")}</h4>
              <span>
                {selectedMaintenanceRequest?.contactPersonMobileNumber}
              </span>
            </div>
          </div>
        </section>
      </div>
    </Dialog>
  );
};

export default MaintenanceRequestsDetailsPopup;
